import { useState, useEffect, useCallback, useRef } from 'react'
import { SortingState, ColumnFiltersState } from '@tanstack/react-table'
import { isEqual } from 'lodash'
import { useAppsSearchParams } from '@wellesley/router'

interface UseTableStateProps {
  initialPageSize?: number
  initialSorting?: SortingState
  initialFilters?: ColumnFiltersState
  namespace?: string
}

const createParamKey = (key: string, namespace?: string): string => {
  return namespace ? `${namespace}_${key}` : key
}

function useTableState({
  initialPageSize = 10,
  initialSorting = [],
  initialFilters = [],
  namespace,
}: UseTableStateProps = {}) {
  const [searchParams, setSearchParams] = useAppsSearchParams()
  const isInitialMount = useRef(true)

  const LIMIT_KEY = createParamKey('limit', namespace)
  const OFFSET_KEY = createParamKey('offset', namespace)
  const SORT_KEY = createParamKey('sort_key', namespace)
  const SORT_DIR_KEY = createParamKey('sort_dir', namespace)

  const RESERVED_PARAMS = [LIMIT_KEY, OFFSET_KEY, SORT_KEY, SORT_DIR_KEY]

  const isInNamespace = useCallback(
    (key: string) => {
      if (!namespace) return !key.includes('_')
      return key.startsWith(`${namespace}_`)
    },
    [namespace],
  )

  const getFiltersFromUrl = useCallback(
    (params: URLSearchParams): ColumnFiltersState => {
      const filters: ColumnFiltersState = [...initialFilters]

      params.forEach((value, key) => {
        if (!RESERVED_PARAMS.includes(key) && isInNamespace(key)) {
          const filterId = namespace ? key.replace(`${namespace}_`, '') : key
          filters.push({ id: filterId, value })
        }
      })

      return filters
    },
    [RESERVED_PARAMS, initialFilters, isInNamespace, namespace],
  )

  const [filters, setFiltersState] = useState(() =>
    getFiltersFromUrl(searchParams),
  )

  const getPageIndexFromUrl = useCallback(
    (params: URLSearchParams): number => {
      const indexParam = params.get(OFFSET_KEY)
      return indexParam ? parseInt(indexParam, 10) : 0
    },
    [OFFSET_KEY],
  )

  const [pageIndex, setPageIndexState] = useState(() =>
    getPageIndexFromUrl(searchParams),
  )

  const getPageSizeFromUrl = useCallback(
    (params: URLSearchParams, defaultSize: number): number => {
      const sizeParam = params.get(LIMIT_KEY)
      return sizeParam ? parseInt(sizeParam, 10) : defaultSize
    },
    [LIMIT_KEY],
  )

  const [pageSize, setPageSizeState] = useState(() =>
    getPageSizeFromUrl(searchParams, initialPageSize),
  )

  const getSortingFromUrl = useCallback(
    (params: URLSearchParams): SortingState => {
      const sortKey = params.get(SORT_KEY)
      const sortDir = params.get(SORT_DIR_KEY)

      if (sortKey && (sortDir === 'asc' || sortDir === 'desc')) {
        return [{ id: sortKey, desc: sortDir === 'desc' }]
      }
      return initialSorting
    },
    [SORT_KEY, SORT_DIR_KEY, initialSorting],
  )

  const [sorting, setSortingState] = useState<SortingState>(() =>
    getSortingFromUrl(searchParams),
  )

  const updateUrl = useCallback(
    (updates: Record<string, string | null>, replace = false) => {
      const params = new URLSearchParams(searchParams)

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key)
        } else {
          params.set(key, value)
        }
      })

      setSearchParams(params, { replace })
    },
    [searchParams, setSearchParams],
  )

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const urlFilters = getFiltersFromUrl(searchParams)
    if (!isEqual(urlFilters, filters)) {
      setFiltersState(urlFilters)
    }

    const urlPageIndex = getPageIndexFromUrl(searchParams)
    if (urlPageIndex !== pageIndex) {
      setPageIndexState(urlPageIndex)
    }

    const urlPageSize = getPageSizeFromUrl(searchParams, pageSize)
    if (urlPageSize !== pageSize) {
      setPageSizeState(urlPageSize)
    }

    const urlSorting = getSortingFromUrl(searchParams)
    const hasSortParams = searchParams.has(SORT_KEY)

    if (hasSortParams && urlSorting.length > 0) {
      const urlSort = urlSorting[0]
      const currentSort = sorting.length > 0 ? sorting[0] : null

      if (
        !currentSort ||
        currentSort.id !== urlSort.id ||
        currentSort.desc !== urlSort.desc
      ) {
        setSortingState(urlSorting)
      }
    }
  }, [
    searchParams,
    getFiltersFromUrl,
    getPageIndexFromUrl,
    getPageSizeFromUrl,
    getSortingFromUrl,
    filters,
    pageIndex,
    pageSize,
    sorting,
    SORT_KEY,
  ])

  const setFilters = useCallback(
    (newFilters: ColumnFiltersState) => {
      if (isEqual(newFilters, filters)) return

      setFiltersState(newFilters)
      setPageIndexState(0)

      const updates: Record<string, string | null> = {}

      for (const key of Array.from(searchParams.keys())) {
        if (!RESERVED_PARAMS.includes(key) && isInNamespace(key)) {
          updates[key] = null
        }
      }

      newFilters.forEach((filter) => {
        if (typeof filter.id === 'string' && filter.value !== undefined) {
          const paramKey = namespace ? `${namespace}_${filter.id}` : filter.id
          updates[paramKey] = String(filter.value)
        }
      })

      updates[OFFSET_KEY] = '0'
      updateUrl(updates)
    },
    [
      searchParams,
      updateUrl,
      filters,
      OFFSET_KEY,
      RESERVED_PARAMS,
      isInNamespace,
      namespace,
    ],
  )

  const setPageIndex = useCallback(
    (index: number) => {
      if (index === pageIndex) return

      setPageIndexState(index)
      updateUrl({ [OFFSET_KEY]: index.toString() })
    },
    [updateUrl, pageIndex, OFFSET_KEY],
  )

  const setPageSize = useCallback(
    (size: number) => {
      if (size === pageSize) return

      const firstVisibleItemIndex = pageIndex * pageSize
      const newPageIndex = Math.floor(firstVisibleItemIndex / size)

      setPageSizeState(size)
      setPageIndexState(newPageIndex)

      updateUrl(
        {
          [LIMIT_KEY]: size.toString(),
          [OFFSET_KEY]: newPageIndex.toString(),
        },
        false,
      )
    },
    [pageIndex, pageSize, updateUrl, LIMIT_KEY, OFFSET_KEY],
  )

  const setSorting = useCallback(
    (newSorting: SortingState) => {
      if (isEqual(newSorting, sorting)) return

      setSortingState(newSorting)

      const updates: Record<string, string | null> = {}

      if (newSorting.length > 0) {
        const sortItem = newSorting[0]
        updates[SORT_KEY] = sortItem.id
        updates[SORT_DIR_KEY] = sortItem.desc ? 'desc' : 'asc'
      } else {
        updates[SORT_KEY] = null
        updates[SORT_DIR_KEY] = null
      }

      updateUrl(updates, false)
    },
    [updateUrl, sorting, SORT_KEY, SORT_DIR_KEY],
  )

  return {
    filters,
    setFilters,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    sorting,
    setSorting,
  }
}

export default useTableState
